/**
 * brenthelsnow: size-charts landing page navigation (item 32 / B16 Phase 1).
 * The server renders EVERY brand and slice; this element hides all but the selection —
 * brand pills → slice pills scoped to that brand → the visible slice. Selection lives in
 * ?brand=&slice= (deep-linkable, back/forward friendly); legacy #brand / #brand-slice
 * hashes from the anchor-nav era still resolve. No JS = the full stacked page (the pills
 * are hidden via :not(:defined) CSS, nothing is hidden server-side).
 */
import { getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

class SizeChartsIndex extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('popstate', this.#handlePopstate);
    const { brand, slice } = this.#selectionFromUrl();
    if (brand) this.#apply(brand, slice, 'replace');
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#handlePopstate);
  }

  #brandPanels() {
    return [...this.querySelectorAll('[data-brand-panel]')];
  }

  /** Resolve URL state → a valid selection (fall back to first brand / first slice). */
  #selectionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    let brand = params.get('brand');
    let slice = params.get('slice');

    const hash = window.location.hash.slice(1);
    if (!brand && hash) {
      const target = document.getElementById(hash);
      if (target?.dataset.slicePanel) {
        brand = target.closest('[data-brand-panel]')?.dataset.brandPanel;
        slice = target.dataset.slicePanel;
      } else if (target?.dataset.brandPanel) {
        brand = target.dataset.brandPanel;
      }
    }
    return this.#validate(brand, slice);
  }

  #validate(brand, slice) {
    const panels = this.#brandPanels();
    const panel = panels.find((p) => p.dataset.brandPanel === brand) ?? panels[0];
    if (!panel) return { brand: null, slice: null };
    const slices = [...panel.querySelectorAll('[data-slice-panel]')];
    const slicePanel = slices.find((s) => s.dataset.slicePanel === slice) ?? slices[0];
    return { brand: panel.dataset.brandPanel, slice: slicePanel?.dataset.slicePanel ?? null };
  }

  /** @param {'push' | 'replace' | null} historyMode - null = URL already reflects it (popstate) */
  #apply(brand, slice, historyMode) {
    for (const panel of this.#brandPanels()) {
      const active = panel.dataset.brandPanel === brand;
      panel.hidden = !active;
      if (!active) continue;
      for (const slicePanel of panel.querySelectorAll('[data-slice-panel]')) {
        slicePanel.hidden = slicePanel.dataset.slicePanel !== slice;
      }
    }
    for (const button of this.querySelectorAll('button[data-brand]')) {
      button.setAttribute('aria-pressed', `${button.dataset.brand === brand}`);
    }
    for (const button of this.querySelectorAll('button[data-slice]')) {
      button.setAttribute('aria-pressed', `${button.dataset.slice === slice}`);
    }
    this.setAttribute('data-active-brand', brand);

    if (!historyMode) return;
    const url = new URL(window.location.href);
    url.searchParams.set('brand', brand);
    if (slice) url.searchParams.set('slice', slice);
    else url.searchParams.delete('slice');
    url.hash = '';
    // Preserve existing state — scroll-container.js keeps scrollTop in history.state and
    // restores it on pageshow; replacing it with {} would snap back/forward returns to the
    // top (Codex r1). Manual scroll state belongs ONLY to the desktop squeeze layout, where
    // scrollRestoration is 'manual': there a pushed entry carries the LIVE offset (a stale
    // one jumps on forward — Codex r2); on mobile the browser restores natively, so a
    // manual scrollTop would override its better answer — strip it instead (Codex r3).
    const baseState = typeof window.history.state === 'object' && window.history.state !== null
      ? window.history.state
      : {};
    if (historyMode === 'push') {
      let state = baseState;
      if (scrollContainerMediaQuery.matches) {
        state = { ...baseState, scrollTop: getScrollTop() };
      } else if ('scrollTop' in baseState) {
        state = { ...baseState };
        delete state.scrollTop;
      }
      window.history.pushState(state, '', url);
    } else {
      window.history.replaceState(baseState, '', url);
    }
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const brandButton = event.target.closest('button[data-brand]');
    if (brandButton) {
      const next = this.#validate(brandButton.dataset.brand, null);
      if (next.brand) this.#apply(next.brand, next.slice, 'push');
      return;
    }
    const sliceButton = event.target.closest('button[data-slice]');
    if (sliceButton) {
      const next = this.#validate(this.getAttribute('data-active-brand'), sliceButton.dataset.slice);
      if (next.brand) this.#apply(next.brand, next.slice, 'push');
    }
  };

  #handlePopstate = () => {
    const { brand, slice } = this.#selectionFromUrl();
    if (brand) this.#apply(brand, slice, null);
  };
}

if (!customElements.get('size-charts-index')) {
  customElements.define('size-charts-index', SizeChartsIndex);
}
